import type { LucideIcon } from 'lucide-react';
import { Paperclip } from 'lucide-react';
import { attachmentKind, type AttachmentViewer } from '../case-room/attachment-kind';
import type { EventAttachment } from '../case-room/event-attachments';

interface StoryAttachmentKindsProps {
  readonly attachments: EventAttachment[];
}

interface KindBadge {
  readonly key: string;
  readonly label: string;
  readonly Icon: LucideIcon;
  readonly color: string;
  readonly count: number;
}

export function StoryAttachmentKinds({ attachments }: StoryAttachmentKindsProps) {
  if (attachments.length === 0) return null;
  const badges = buildKindBadges(attachments);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {badges.map((badge) => (
        <span
          key={badge.key}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
          style={{ background: `${badge.color}24`, color: badge.color }}
          title={`${badge.count} ${badge.label.toLowerCase()}`}
        >
          <badge.Icon size={11} />
          <span>{badge.label}</span>
          {badge.count > 1 && <span>{badge.count}</span>}
        </span>
      ))}
      <span className="inline-flex items-center gap-1 text-[11px] text-text-tertiary">
        <Paperclip size={11} />
        {attachments.length}
      </span>
    </div>
  );
}

function buildKindBadges(attachments: EventAttachment[]): KindBadge[] {
  const map = new Map<string, KindBadge>();
  for (const attachment of attachments) {
    const kind = attachmentKind(attachment);
    const label = labelForViewer(kind.viewer);
    const key = `${kind.viewer}:${kind.Icon.displayName ?? kind.Icon.name ?? label}`;
    const existing = map.get(key);
    if (existing) {
      map.set(key, { ...existing, count: existing.count + 1 });
      continue;
    }
    map.set(key, {
      key,
      label,
      Icon: kind.Icon,
      color: kind.color,
      count: 1,
    });
  }
  return Array.from(map.values());
}

function labelForViewer(viewer: AttachmentViewer): string {
  if (viewer === 'image') return 'Bilde';
  if (viewer === 'pdf') return 'PDF';
  if (viewer === 'video') return 'Video';
  if (viewer === 'audio') return 'Lyd';
  if (viewer === 'text') return 'Tekst';
  return 'Fil';
}
